import type {
  AssistantCallMember,
  AssistantCallMessage,
  AssistantCallView,
  AssistantRingAnswer,
} from '@crowdex/agent-contracts/api';
import {
  createRealtimeCall,
  type RealtimeAnswer,
  type RealtimeCall,
  type RealtimeEvent,
  realtimeFragment,
} from '@crowdex/provider-contracts/realtime-call';
import { useCallback, useEffect, useRef, useState } from 'react';
import { commandCall, loadAssistants, offerSession, startCall } from './call-api';
import { setCallLevel } from './call-level';
import { addCallSpeech, clearCallSpeech } from './call-speech';
import { addWords, type TranscriptLine } from './call-transcript';
import { monitorSpeech } from './monitor-speech';

export type LiveCallStatus = 'idle' | 'connecting' | 'listening' | 'speaking' | 'working';

const EMPTY_VIEW: AssistantCallView = {
  working: false,
  activity: null,
  steps: [],
  followed: [],
  request: null,
  assistant: null,
  speaker: null,
  assistants: [],
  persona: null,
  personas: [],
  group: null,
  groupSpeaker: null,
  groupThinking: null,
  error: null,
};

const errorMessage = (reason: unknown) =>
  reason instanceof Error ? reason.message : String(reason);
const language = () => (typeof navigator === 'undefined' ? 'en' : navigator.language);

/**
 * The user's live call with one of their assistants (docs/adr-assistants.md): the one the user
 * picked, else the one whose conversation is on screen (`placeAssistant`), else the home one. The
 * server runs it (`/api/assistant/calls`): it drives the voice (on the session's sideband, or as
 * participants of the call's room), hands tasks to the assistant's channel, speaks its replies,
 * asks what its turns wait on, hands the call over to another assistant or makes it a group call,
 * and keeps what the user asked going after they hang up. The page holds the media (the
 * microphone, the voices it hears), shows what is said as it is said and what the server tells it
 * (`AssistantCallMessage`), and sends what the user does. The call lives above the
 * conversations: switching between them keeps it, and the conversation on screen is the one it
 * is about.
 */
export function useLiveCall({
  openConversation,
  openWorkspace,
  placeAssistant = null,
}: {
  openConversation: string | null;
  /** The folder on screen, where the assistant starts when the open conversation is new. */
  openWorkspace: string | null;
  /** The assistant the conversation on screen belongs to (its channel or its work), if any. */
  placeAssistant?: string | null;
}) {
  const [connecting, setConnecting] = useState(false);
  const [active, setActive] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptLine[]>([]);
  const [view, setViewState] = useState<AssistantCallView>(EMPTY_VIEW);
  /** The server's latest view of the call, read as it ends. */
  const shownView = useRef<AssistantCallView>(EMPTY_VIEW);
  const setView = useCallback((next: AssistantCallView) => {
    shownView.current = next;
    setViewState(next);
  }, []);
  /** Who the user can talk to before a call: the server's view replaces it during one. */
  const [known, setKnown] = useState<AssistantCallMember[]>([]);
  /** The assistant the user picked to call, in the place they picked it in. */
  const [chosen, setChosen] = useState<{ id: string; place: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const callId = useRef<string | null>(null);
  const call = useRef<RealtimeCall | null>(null);
  const opening = useRef<AbortController | null>(null);
  const microphone = useRef<MediaStream | null>(null);
  /** The voices the page hears (a room's personas each have their own), and which speak now. */
  const voices = useRef(
    new Map<string, { audio: HTMLAudioElement; monitor: { close(): void } | null }>(),
  );
  const speakingVoices = useRef(new Set<string>());
  const stream = useRef<EventSource | null>(null);
  const mutedRef = useRef(false);
  // Another place calls its own assistant: what the user picked was for the one they left.
  const target = chosen !== null && chosen.place === placeAssistant ? chosen.id : placeAssistant;
  const latest = useRef({ openConversation, openWorkspace, target });
  latest.current = { openConversation, openWorkspace, target };

  const listen = useCallback(() => {
    for (const track of microphone.current?.getAudioTracks() ?? [])
      track.enabled = !mutedRef.current;
  }, []);

  /** What the call's session says: shown as it is said, shaping the face's mouth. */
  const onEvent = useCallback((event: RealtimeEvent, own: RealtimeCall | null) => {
    if (own !== call.current) return;
    const said = realtimeFragment(event);
    if (said === null) return;
    if (said.role === 'assistant') addCallSpeech(said.text);
    setTranscript((lines) => addWords(lines, said));
  }, []);

  /** A voice of the call, played and followed (a room has one per persona). */
  const hear = useCallback((remote: MediaStream, voice = 'call') => {
    const known = voices.current.get(voice);
    known?.monitor?.close();
    const audio = known?.audio ?? new Audio();
    audio.autoplay = true;
    audio.srcObject = remote;
    void audio.play().catch(() => undefined);
    const monitor = monitorSpeech(
      remote,
      (now) => {
        if (now) speakingVoices.current.add(voice);
        else speakingVoices.current.delete(voice);
        setSpeaking(speakingVoices.current.size > 0);
      },
      setCallLevel,
    );
    voices.current.set(voice, { audio, monitor });
  }, []);

  /** Leaves the call's media: its session and its voices; hanging up (`ending`), the microphone. */
  const closeMedia = useCallback((ending: boolean) => {
    const closing = call.current;
    call.current = null;
    if (closing !== null) void closing.close();
    for (const voice of voices.current.values()) {
      voice.monitor?.close();
      voice.audio.srcObject = null;
    }
    voices.current.clear();
    speakingVoices.current.clear();
    setSpeaking(false);
    setCallLevel(0);
    clearCallSpeech();
    if (!ending) return;
    for (const track of microphone.current?.getTracks() ?? []) track.stop();
    microphone.current = null;
  }, []);

  /** The call ended (the user hung up here or elsewhere, or it failed): back to idle. */
  const ended = useCallback(
    (reason?: string) => {
      opening.current?.abort();
      opening.current = null;
      stream.current?.close();
      stream.current = null;
      closeMedia(true);
      callId.current = null;
      setActive(false);
      setConnecting(false);
      setMuted(false);
      mutedRef.current = false;
      setTranscript([]);
      // Who the user can talk to, as the call left it.
      const last = shownView.current;
      if (last.assistants.length > 0) setKnown(last.assistants);
      setView(EMPTY_VIEW);
      void loadAssistants().then(setKnown, () => undefined);
      if (reason) setError(reason);
    },
    [closeMedia, setView],
  );

  /**
   * A session of the call for the page's media: the first one (`startCall`) or the next one the
   * server asked for (`offerSession`), which answers the page's offer or names its room.
   */
  const join = useCallback(
    async (
      signaling: (sdp: string) => Promise<RealtimeAnswer | null>,
      signal: AbortSignal,
    ): Promise<boolean> => {
      const stream = microphone.current;
      if (stream === null) return false;
      let own: RealtimeCall | null = null;
      own = await createRealtimeCall({
        signaling: async (sdp) => {
          const answer = await signaling(sdp);
          if (answer === null) throw new DOMException('Cancelled', 'AbortError');
          return answer;
        },
        stream,
        signal,
        onEvent: (event) => onEvent(event, own ?? call.current),
        onRemoteStream: hear,
      });
      call.current = own;
      return true;
    },
    [hear, onEvent],
  );

  /** What the server tells the call's page. */
  const onMessage = useCallback(
    (message: AssistantCallMessage) => {
      switch (message.type) {
        case 'view':
          setView(message.view);
          return;
        case 'renegotiate': {
          const id = callId.current;
          if (id === null) return;
          // The voice goes on in a new session: the microphone and the screen stay.
          closeMedia(false);
          const controller = new AbortController();
          opening.current = controller;
          void join(
            async (sdp) => (await offerSession(id, { sdp })) as RealtimeAnswer,
            controller.signal,
          )
            .catch((reason: unknown) => {
              if (!controller.signal.aborted) {
                setError(errorMessage(reason));
                void commandCall(id, { type: 'hangup' }).catch(() => undefined);
                ended();
              }
            })
            .finally(() => {
              if (opening.current === controller) opening.current = null;
            });
          return;
        }
        case 'ended':
          ended(message.error);
          return;
      }
    },
    [closeMedia, ended, join, setView],
  );

  /**
   * Starts the call with `assistant` (the one the user picked, else the place's, else home);
   * answering the assistant's `ring`, it says first what the assistant calls about (another page
   * may have answered it already: then nothing starts), as the assistant that called.
   */
  const start = useCallback(
    async (
      options: { ring?: string; assistant?: string } = {},
    ): Promise<AssistantRingAnswer | null> => {
      if (callId.current !== null || opening.current !== null) return null;
      const controller = new AbortController();
      opening.current = controller;
      setConnecting(true);
      setError(null);
      const answered: { ring: AssistantRingAnswer | null } = { ring: null };
      try {
        microphone.current ??= await navigator.mediaDevices.getUserMedia({
          audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
          video: false,
        });
        if (controller.signal.aborted) return null;
        const open = latest.current;
        const assistant =
          options.ring === undefined ? (options.assistant ?? open.target ?? undefined) : undefined;
        await join(async (sdp) => {
          const started = await startCall({
            sdp,
            language: language(),
            ...(assistant === undefined ? {} : { assistant }),
            ...(options.ring === undefined ? {} : { ring: options.ring }),
            ...(open.openConversation === null ? {} : { openConversation: open.openConversation }),
            ...(open.openWorkspace === null ? {} : { openWorkspace: open.openWorkspace }),
          });
          answered.ring = started.ring;
          if (started.call === null) return null;
          callId.current = started.call;
          setView(started.view);
          const messages = new EventSource(
            `/api/assistant/calls/${encodeURIComponent(started.call)}/stream`,
          );
          messages.onmessage = ({ data }) => {
            try {
              onMessage(JSON.parse(String(data)) as AssistantCallMessage);
            } catch {}
          };
          stream.current = messages;
          return started.answer as RealtimeAnswer;
        }, controller.signal);
        if (controller.signal.aborted) return null;
        setActive(true);
        return answered.ring;
      } catch (reason) {
        if (controller.signal.aborted) return null;
        const id = callId.current;
        if (id !== null) void commandCall(id, { type: 'hangup' }).catch(() => undefined);
        const gone = answered.ring?.status === 'gone';
        ended(
          gone ? 'The call was answered elsewhere, or it stopped ringing.' : errorMessage(reason),
        );
        return null;
      } finally {
        if (opening.current === controller) opening.current = null;
        setConnecting(false);
      }
    },
    [ended, join, onMessage, setView],
  );

  /** Ends the call. Nothing the user asked is lost: the server keeps it going. */
  const hangUp = useCallback(() => {
    const id = callId.current;
    if (id !== null) void commandCall(id, { type: 'hangup' }).catch(() => undefined);
    ended();
  }, [ended]);

  const command = useCallback((next: Parameters<typeof commandCall>[1]) => {
    const id = callId.current;
    if (id === null) return;
    void commandCall(id, next).catch((reason: unknown) => setError(errorMessage(reason)));
  }, []);

  useEffect(() => hangUp, [hangUp]);

  // Who the user can talk to shows before a call too, again as the page comes back to them.
  useEffect(() => {
    const refresh = () => {
      if (callId.current === null && opening.current === null)
        void loadAssistants().then(setKnown, () => undefined);
    };
    refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);

  // Closing the page hangs up at once.
  useEffect(() => {
    window.addEventListener('pagehide', hangUp);
    return () => window.removeEventListener('pagehide', hangUp);
  }, [hangUp]);

  // Another conversation opened during the call is the one it is about from now on.
  useEffect(() => {
    if (!active) return;
    command({ type: 'focus', conversation: openConversation, openWorkspace });
  }, [active, command, openConversation, openWorkspace]);

  const toggleMute = useCallback(() => {
    mutedRef.current = !mutedRef.current;
    setMuted(mutedRef.current);
    listen();
  }, [listen]);

  const inCall = active || connecting;
  /** Who a call reaches before one: the picked one, else the place's, else home. */
  const reached =
    known.find((entry) => entry.id === target) ??
    known.find((entry) => entry.home) ??
    known[0] ??
    null;
  const shown: AssistantCallView = inCall
    ? view
    : {
        ...EMPTY_VIEW,
        assistants: known,
        speaker: reached?.id ?? null,
        assistant: reached?.conversation ?? null,
      };
  const status: LiveCallStatus = connecting
    ? 'connecting'
    : !active
      ? 'idle'
      : speaking
        ? 'speaking'
        : shown.working
          ? 'working'
          : 'listening';
  return {
    status,
    muted,
    transcript,
    activity: shown.activity,
    steps: shown.steps,
    followed: shown.followed,
    request: shown.request,
    /** The channel of the assistant the call speaks as (or reaches, before one). */
    assistant: shown.assistant,
    /** The assistant the call speaks as (or reaches, before one). */
    speaker: shown.speaker,
    /** The user's assistants, home first. */
    assistants: shown.assistants,
    error: error ?? shown.error,
    start,
    hangUp,
    /** Hands the call over to another assistant; out of a call, picks the one it reaches. */
    switchTo: (id: string) => {
      if (callId.current !== null) command({ type: 'switch', assistant: id });
      else if (opening.current === null) setChosen({ id, place: placeAssistant });
    },
    /** The group call, who speaks in it now, and who prepares an answer. */
    group: shown.group,
    groupSpeaker: shown.groupSpeaker,
    groupThinking: shown.groupThinking,
    /** Brings an assistant into the call: a group call with the one on the line (or the group's). */
    invite: (id: string) => command({ type: 'invite', assistant: id }),
    toggleMute,
    answer: (approved: boolean) => command({ type: 'answer', approved }),
    /** Sends what the user typed during the call: its reply is spoken. */
    type: (text: string) => command({ type: 'type', text }),
    dismissError: () => setError(null),
  };
}

export type LiveCall = ReturnType<typeof useLiveCall>;
